import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui-kit";
import { X, ArrowRight, ArrowLeft } from "lucide-react";

// First-run guided tour. Shows once per storageKey; can be reopened via forceOpen.
export default function OnboardingTour({ steps, storageKey, forceOpen = false, suppressAuto = false, onClose }) {
  const [open, setOpen] = useState(false);
  const [i, setI] = useState(0);

  useEffect(() => {
    if (!suppressAuto && !localStorage.getItem(storageKey)) setOpen(true);
  }, [storageKey, suppressAuto]);

  useEffect(() => {
    if (forceOpen) { setOpen(true); setI(0); }
  }, [forceOpen]);

  const finish = () => {
    localStorage.setItem(storageKey, "1");
    setOpen(false);
    setI(0);
    onClose && onClose();
  };

  const step = steps[i];
  const last = i === steps.length - 1;

  return (
    <AnimatePresence>
      {open && step && (
        <motion.div
          className="fixed inset-0 z-[70] grid place-items-center p-5"
          style={{ background: "rgba(20,30,26,0.5)", backdropFilter: "blur(4px)" }}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          data-testid="onboarding-tour"
        >
          <motion.div
            initial={{ scale: 0.94, y: 16 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, opacity: 0 }}
            transition={{ type: "spring", stiffness: 240, damping: 22 }}
            className="bg-[var(--ns-surface)] rounded-[var(--ns-radius)] border border-[var(--ns-border)] w-full max-w-md p-7 ns-card relative"
          >
            <button onClick={finish} data-testid="onboarding-skip"
              className="absolute top-4 right-4 text-[var(--ns-subtle)] hover:text-[var(--ns-text)]" aria-label="Skip tour">
              <X size={20} />
            </button>

            <AnimatePresence mode="wait">
              <motion.div key={i} initial={{ opacity: 0, x: 18 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -18 }}
                transition={{ duration: 0.22 }} className="text-center">
                <div className="w-16 h-16 mx-auto rounded-2xl grid place-items-center mb-4"
                  style={{ background: "var(--ns-primary-light)", color: "var(--ns-primary)" }}>
                  <step.icon size={30} />
                </div>
                <h3 className="font-head text-xl font-bold text-[var(--ns-text)]">{step.title}</h3>
                <p className="mt-2 text-sm text-[var(--ns-muted)] leading-relaxed">{step.body}</p>
              </motion.div>
            </AnimatePresence>

            <div className="flex items-center justify-center gap-1.5 my-5" data-testid="onboarding-dots">
              {steps.map((_, idx) => (
                <span key={idx} className="rounded-full transition-all duration-300"
                  style={{ width: idx === i ? 20 : 7, height: 7, background: idx === i ? "var(--ns-primary)" : "var(--ns-border)" }} />
              ))}
            </div>

            <div className="flex items-center justify-between gap-3">
              {i > 0 ? (
                <Button variant="ghost" data-testid="onboarding-back" onClick={() => setI(i - 1)}><ArrowLeft size={16} /> Back</Button>
              ) : (
                <button onClick={finish} data-testid="onboarding-skip-text" className="text-sm font-semibold text-[var(--ns-subtle)] px-3">Skip</button>
              )}
              {last ? (
                <Button data-testid="onboarding-done" onClick={finish}>Get started <ArrowRight size={16} /></Button>
              ) : (
                <Button data-testid="onboarding-next" onClick={() => setI(i + 1)}>Next <ArrowRight size={16} /></Button>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
